/**
 * Cheap collision for the world: buildings as boxes, trunks and posts as
 * circles, and round bodies pushed out of them.
 *
 * Nothing here is a physics engine. A building is its footprint's oriented
 * box from the ground to its eaves; a tree is its trunk; a lamp is its post.
 * A walker or a car is one or two circles in plan, with a height range, and
 * is pushed out of whatever it overlaps in that range — so it slides along a
 * wall rather than stopping dead, and walks under what is overhead.
 *
 * Built in the placement worker, where the tile's heights are known, as a
 * flat array; indexed on the main thread in a spatial hash per tile.
 */

import { SPECIES } from '../biome/flora.js';
import { SEMI_TRAILER_BOX, VEHICLE_SIZE } from './parking.js';

/**
 * Floats per collider: kind, cx, cz, ux, uz, hw, hd, bottom, top, species. A
 * polygon keeps its centre in cx, cz, its bounding radius in hw, and in ux,
 * uz the first point and number of points of its outline in the points
 * array. `species` is the prop's or tree's id in `SPECIES`, so a hit can be
 * told from a building's (-1).
 */
export const COLLIDER_STRIDE = 10;
const BOX = 0;
const CIRCLE = 1;
const POLYGON = 2;

/** A tile's colliders: fixed records, and the outline points polygons index. */
export interface Colliders {
  records: Float32Array;
  points: Float32Array;
  /** Initial records belonging to buildings, before props and structural walls. */
  buildingCount?: number;
}

/** Trunk and post radii by species, metres at scale 1; absent species do not collide. */
const RADIUS: Partial<Record<(typeof SPECIES)[number], number>> = {
  pine: 0.22, spruce: 0.24, snowySpruce: 0.24, pineSnowy: 0.22, tallPine: 0.15, tallPineSnowy: 0.15, birch: 0.16, birchAutumn: 0.16, birchBare: 0.16,
  broadleaf: 0.3, broadleafAutumn: 0.3, broadleafBare: 0.3, jungle: 0.35, palm: 0.2, acacia: 0.25,
  olive: 0.25, cypress: 0.2, cactus: 0.3, rock: 0.7, stump: 0.25,
  lamp: 0.14, fountain: 2.2, memorial: 0.5, tower: 1.4, postbox: 0.25, bin: 0.25,
};
/**
 * Pieces of hedge: boxes along their yaw, half length and half
 * thickness at scale 1, and their height. Thin, but a body's circle is wider.
 */
const WALL: Partial<Record<(typeof SPECIES)[number], { hw: number; hd: number; top: number }>> = {
  hedge: { hw: 2.05, hd: 0.35, top: 0.95 },
  bench: { hw: 0.9, hd: 0.25, top: 0.9 },
  picket: { hw: 2, hd: 0.1, top: 1.1 },
  rail: { hw: 2, hd: 0.1, top: 1.05 },
  // Parked vehicles: their box, across (x) and along (z) the body.
  ...Object.fromEntries(Object.entries(VEHICLE_SIZE).map(([name, v]) =>
    [name, { hw: v.halfWidth, hd: v.halfLength, top: v.height }])),
};
/**
 * A bus shelter: its three panes of glass, as thin boxes in its own frame
 * (x across, z back): open at the front, so people can stand inside, and
 * a car is in it where it meets the glass. Middle x, z, half sizes, height.
 */
const SHELTER_PANES = [
  { x: 0, z: 0.7, hw: 1.6, hd: 0.06 },
  { x: -1.58, z: 0, hw: 0.06, hd: 0.7 },
  { x: 1.58, z: 0, hw: 0.06, hd: 0.7 },
];
const SHELTER_TOP = 2.4;

/** How tall a species stands, metres at scale 1: bodies above it pass over. */
const HEIGHT: Partial<Record<(typeof SPECIES)[number], number>> = {
  rock: 1.0, stump: 0.5, postbox: 1.1, bin: 0.9, fountain: 0.6, memorial: 2.2,
};

export interface ColliderInput {
  /** Four corners per building range, in order round the footprint. */
  probes: Float32Array;
  /** [start, count] per building range, and the grounded positions. */
  ranges: Uint32Array;
  positions: Float32Array;
  /** Only the first this many ranges are solid buildings. */
  solidCount: number;
  /** The true outline of each building not built as its box: points, and [start, count] per range. */
  outlines?: Float32Array;
  outlineRanges?: Uint32Array;
  /**
   * Points along the carriageways, x, z pairs, a couple of metres apart.
   * Nothing on a carriageway is solid: a road running through a building
   * goes through a passage in it, and a post mapped onto a road is a mapping
   * error, and neither should stop the car.
   */
  carriageway?: Float32Array;
  /** Placed trees and props: x, y, z; scale; species. */
  treePositions: Float32Array;
  treeScales: Float32Array;
  treeSpecies: Uint8Array;
  /** Yaw per tree or prop, for the pieces that collide as boxes. */
  treeRotations?: Float32Array;
  /** Tunnel and portal walls: x0, z0, x1, z1, bottom, top per wall. See `tunnelmesh.ts`. */
  walls?: Float32Array;
}

/** Half the thickness a tunnel's wall collides as, metres. */
export const WALL_HALF = 0.3;

export function buildColliders(input: ColliderInput): Colliders {
  const out: number[] = [];
  const points: number[] = [];
  const road = hashPoints(input.carriageway);
  const onRoad = (cx: number, cz: number, r: number, inside: (x: number, z: number) => boolean) => {
    for (const [x, z] of road.near(cx, cz, r)) if (inside(x, z)) return true;
    return false;
  };
  const { probes, ranges, positions } = input;
  for (let b = 0; b < input.solidCount; b++) {
    const start = ranges[b * 2], count = ranges[b * 2 + 1];
    if (count === 0) continue;
    let bottom = Infinity, top = -Infinity;
    for (let v = start; v < start + count; v++) {
      const y = positions[v * 3 + 1];
      if (y < bottom) bottom = y;
      if (y > top) top = y;
    }
    // A building that is not its box collides as its outline: the box of an
    // L-plan or a long, bent block reaches out over the street beside it.
    const count2 = input.outlineRanges?.[b * 2 + 1] ?? 0;
    if (count2 >= 3 && input.outlines !== undefined) {
      const first = input.outlineRanges![b * 2];
      let cx = 0, cz = 0;
      for (let k = 0; k < count2; k++) { cx += input.outlines[(first + k) * 2]; cz += input.outlines[(first + k) * 2 + 1]; }
      cx /= count2; cz /= count2;
      let r = 0;
      for (let k = 0; k < count2; k++) {
        r = Math.max(r, Math.hypot(input.outlines[(first + k) * 2] - cx, input.outlines[(first + k) * 2 + 1] - cz));
      }
      const outline = input.outlines;
      const inPolygon = (x: number, z: number) => {
        let inside = false;
        for (let i = 0, j = count2 - 1; i < count2; j = i++) {
          const ax = outline[(first + j) * 2], az = outline[(first + j) * 2 + 1];
          const bx = outline[(first + i) * 2], bz = outline[(first + i) * 2 + 1];
          if ((bz > z) !== (az > z) && x < ((ax - bx) * (z - bz)) / (az - bz) + bx) inside = !inside;
        }
        return inside;
      };
      if (onRoad(cx, cz, r, inPolygon)) continue;
      out.push(POLYGON, cx, cz, points.length / 2, count2, r, 0, bottom, top, -1);
      for (let k = 0; k < count2 * 2; k++) points.push(input.outlines[first * 2 + k]);
      continue;
    }
    const c = probes.subarray(b * 8, b * 8 + 8);
    const e1x = c[2] - c[0], e1z = c[3] - c[1], e2x = c[6] - c[0], e2z = c[7] - c[1];
    const l1 = Math.hypot(e1x, e1z), l2 = Math.hypot(e2x, e2z);
    if (l1 < 0.3 || l2 < 0.3) continue;
    const bcx = (c[0] + c[2] + c[4] + c[6]) / 4, bcz = (c[1] + c[3] + c[5] + c[7]) / 4;
    const inBox = (x: number, z: number) => {
      const dx = x - bcx, dz = z - bcz;
      return Math.abs(dx * e1x + dz * e1z) / l1 <= l1 / 2 && Math.abs(-dx * e1z + dz * e1x) / l1 <= l2 / 2;
    };
    if (onRoad(bcx, bcz, Math.hypot(l1, l2) / 2, inBox)) continue;
    out.push(BOX, (c[0] + c[2] + c[4] + c[6]) / 4, (c[1] + c[3] + c[5] + c[7]) / 4,
      e1x / l1, e1z / l1, l1 / 2, l2 / 2, bottom, top, -1);
  }
  const buildingCount = out.length / COLLIDER_STRIDE;
  for (let i = 0; i < input.treeSpecies.length; i++) {
    const species = SPECIES[input.treeSpecies[i]];
    if (species === 'shelter') {
      const scale = input.treeScales[i] || 1;
      const yaw = input.treeRotations?.[i] ?? 0;
      const tx = input.treePositions[i * 3], y = input.treePositions[i * 3 + 1], tz = input.treePositions[i * 3 + 2];
      if (onRoad(tx, tz, 2.5, (x, z) => Math.hypot(x - tx, z - tz) < 2)) continue;
      // Local +X is (cos yaw, -sin yaw) on the ground, local +Z (sin yaw, cos yaw).
      const c = Math.cos(yaw), s = Math.sin(yaw);
      for (const pane of SHELTER_PANES) {
        const px = tx + (pane.x * c + pane.z * s) * scale, pz = tz + (-pane.x * s + pane.z * c) * scale;
        out.push(BOX, px, pz, c, -s, pane.hw * scale, pane.hd * scale, y - 0.3, y + SHELTER_TOP * scale, input.treeSpecies[i]);
      }
      continue;
    }
    const wall = species === undefined ? undefined : WALL[species];
    if (wall !== undefined) {
      const scale = input.treeScales[i] || 1;
      const yaw = input.treeRotations?.[i] ?? 0;
      const y = input.treePositions[i * 3 + 1];
      // Local +X is (cos yaw, -sin yaw) on the ground.
      out.push(BOX, input.treePositions[i * 3], input.treePositions[i * 3 + 2], Math.cos(yaw), -Math.sin(yaw),
        // The scale stretches a piece along its length alone.
        wall.hw * scale, wall.hd, y - 0.5, y + wall.top, input.treeSpecies[i]);
      // An articulated truck's trailer, straight behind its tractor: back is (sin yaw, cos yaw).
      if (species === 'truckSemi') {
        const bx = input.treePositions[i * 3] + Math.sin(yaw) * SEMI_TRAILER_BOX.behind;
        const bz = input.treePositions[i * 3 + 2] + Math.cos(yaw) * SEMI_TRAILER_BOX.behind;
        out.push(BOX, bx, bz, Math.cos(yaw), -Math.sin(yaw), wall.hw, SEMI_TRAILER_BOX.halfLength, y - 0.5, y + wall.top, input.treeSpecies[i]);
      }
      continue;
    }
    const r = species === undefined ? undefined : RADIUS[species];
    if (r === undefined) continue;
    const scale = input.treeScales[i] || 1;
    const y = input.treePositions[i * 3 + 1];
    const tx = input.treePositions[i * 3], tz = input.treePositions[i * 3 + 2];
    if (onRoad(tx, tz, r * scale + 1.5, (x, z) => Math.hypot(x - tx, z - tz) < r * scale + 1.5)) continue;
    out.push(CIRCLE, input.treePositions[i * 3], input.treePositions[i * 3 + 2], 0, 0, r * scale, 0,
      y - 1, y + (HEIGHT[species] ?? 20) * scale, input.treeSpecies[i]);
  }
  // Tunnel walls: a thin box along each, as solid as a building's. Not
  // skipped on a carriageway: the tunnel's road runs between them.
  const walls = input.walls ?? new Float32Array(0);
  for (let i = 0; i + 5 < walls.length; i += 6) {
    const x0 = walls[i], z0 = walls[i + 1], x1 = walls[i + 2], z1 = walls[i + 3];
    const len = Math.hypot(x1 - x0, z1 - z0);
    if (len < 1e-3) continue;
    out.push(BOX, (x0 + x1) / 2, (z0 + z1) / 2, (x1 - x0) / len, (z1 - z0) / len, len / 2 + 0.05, WALL_HALF, walls[i + 4], walls[i + 5], -1);
  }
  return { records: new Float32Array(out), points: new Float32Array(points), buildingCount };
}

/** Points in a hash, for "any of these near here?". */
function hashPoints(flat: Float32Array | undefined): { near(x: number, z: number, r: number): Iterable<[number, number]> } {
  const CELL = 8;
  const cells = new Map<number, number[]>();
  if (flat !== undefined) {
    for (let i = 0; i + 1 < flat.length; i += 2) {
      const key = Math.floor(flat[i] / CELL) * 100003 + Math.floor(flat[i + 1] / CELL);
      let list = cells.get(key);
      if (list === undefined) cells.set(key, (list = []));
      list.push(i);
    }
  }
  return {
    *near(x: number, z: number, r: number) {
      if (flat === undefined) return;
      for (let gx = Math.floor((x - r) / CELL); gx <= Math.floor((x + r) / CELL); gx++) {
        for (let gz = Math.floor((z - r) / CELL); gz <= Math.floor((z + r) / CELL); gz++) {
          for (const i of cells.get(gx * 100003 + gz) ?? []) yield [flat[i], flat[i + 1]] as [number, number];
        }
      }
    },
  };
}

/** A tile's colliders in a spatial hash, for "what is near here?". */
export class CollisionIndex {
  private readonly cells = new Map<number, number[]>();
  private static readonly CELL = 12;

  private readonly colliders: Float32Array;
  private readonly points: Float32Array;
  private readonly buildingCount: number;
  private readonly seenStamp: Int32Array;
  private stamp = 0;
  private readonly minX: number;
  private readonly maxX: number;
  private readonly minZ: number;
  private readonly maxZ: number;

  constructor(input: Colliders) {
    const colliders = input.records;
    this.colliders = colliders;
    this.points = input.points;
    this.buildingCount = input.buildingCount ?? 0;
    this.seenStamp = new Int32Array(colliders.length / COLLIDER_STRIDE);
    const c = CollisionIndex.CELL;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (let k = 0; k < colliders.length; k += COLLIDER_STRIDE) {
      const cx = colliders[k + 1], cz = colliders[k + 2];
      const r = colliders[k] === BOX ? Math.hypot(colliders[k + 5], colliders[k + 6]) : colliders[k + 5];
      if (cx - r < minX) minX = cx - r;
      if (cx + r > maxX) maxX = cx + r;
      if (cz - r < minZ) minZ = cz - r;
      if (cz + r > maxZ) maxZ = cz + r;
      // (A polygon's bounding radius is in hw too.)
      for (let gx = Math.floor((cx - r) / c); gx <= Math.floor((cx + r) / c); gx++) {
        for (let gz = Math.floor((cz - r) / c); gz <= Math.floor((cz + r) / c); gz++) {
          const key = gx * 100003 + gz;
          let list = this.cells.get(key);
          if (list === undefined) this.cells.set(key, (list = []));
          list.push(k);
        }
      }
    }
    this.minX = minX;
    this.maxX = maxX;
    this.minZ = minZ;
    this.maxZ = maxZ;
  }

  /**
   * Stop the colliders centred within `r` of (x, z) from colliding: a parked
   * car that has been driven away, a bin knocked over, a hedge flattened.
   * Only boxes, or with `species`, only that species' colliders of any shape.
   * Returns how many it turned off.
   */
  disableNear(x: number, z: number, r: number, species?: number): number {
    const a = this.colliders;
    let n = 0;
    for (let k = 0; k < a.length; k += COLLIDER_STRIDE) {
      if (species === undefined ? a[k] !== BOX : a[k + 9] !== species) continue;
      if (Math.hypot(a[k + 1] - x, a[k + 2] - z) > r) continue;
      a[k + 8] = -Infinity;
      n++;
    }
    return n;
  }

  /**
   * Push a circle at (x, z) of `radius`, spanning `feet`..`head`, out of the
   * colliders it overlaps. Returns the new centre and the total push normal,
   * and how wide the narrowest thing it hit is (a trunk's or a post's
   * diameter, a box's narrower side; a building is as wide as can be), and
   * what that was: its species (-1 a building) and its middle. Undefined
   * when it overlaps nothing. Species `skip` says to pass through: a car
   * through a hedge.
   */
  resolve(x: number, z: number, radius: number, feet: number, head: number, skip?: (species: number) => boolean, inBuildingPassage = false):
    { x: number; z: number; nx: number; nz: number; width: number; species: number; cx: number; cz: number } | undefined {
    if (x + radius < this.minX || x - radius > this.maxX || z + radius < this.minZ || z - radius > this.maxZ) {
      return undefined;
    }
    const c = CollisionIndex.CELL;
    const a = this.colliders;
    const seen = this.seenStamp;
    let px = x, pz = z, pushed = false, width = Infinity, species = -1, cx = 0, cz = 0;
    // Two passes: a push out of one wall can put the body into the next.
    for (let pass = 0; pass < 2; pass++) {
      const s = (this.stamp = (this.stamp + 1) | 0);
      for (let gx = Math.floor((px - radius) / c); gx <= Math.floor((px + radius) / c); gx++) {
        for (let gz = Math.floor((pz - radius) / c); gz <= Math.floor((pz + radius) / c); gz++) {
          const cell = this.cells.get(gx * 100003 + gz);
          if (cell === undefined) continue;
          for (let i = 0; i < cell.length; i++) {
            const k = cell[i];
            const idx = k / COLLIDER_STRIDE;
            if (seen[idx] === s) continue;
            seen[idx] = s;
            if (inBuildingPassage && idx < this.buildingCount) continue;
            // Over it, or under it: no contact.
            if (head <= a[k + 7] || feet >= a[k + 8]) continue;
            if (skip !== undefined && a[k + 9] >= 0 && skip(a[k + 9])) continue;
            const out = a[k] === BOX ? pushOutOfBox(a, k, px, pz, radius)
              : a[k] === POLYGON ? pushOutOfPolygon(this.points, a[k + 3], a[k + 4], px, pz, radius)
                : pushOutOfCircle(a, k, px, pz, radius);
            if (out === undefined) continue;
            px = out[0]; pz = out[1]; pushed = true;
            const w = a[k] === BOX ? 2 * Math.min(a[k + 5], a[k + 6]) : a[k] === POLYGON ? Infinity : 2 * a[k + 5];
            // What was hit is the narrowest thing touched, or a building if only that.
            if (w < width || (width === Infinity && species === -1)) {
              width = Math.min(width, w);
              species = a[k + 9]; cx = a[k + 1]; cz = a[k + 2];
            }
          }
        }
      }
    }
    if (!pushed) return undefined;
    const dx = px - x, dz = pz - z;
    const len = Math.hypot(dx, dz) || 1;
    return { x: px, z: pz, nx: dx / len, nz: dz / len, width, species, cx, cz };
  }
}

function pushOutOfCircle(a: Float32Array, k: number, x: number, z: number, radius: number): [number, number] | undefined {
  const dx = x - a[k + 1], dz = z - a[k + 2];
  const need = radius + a[k + 5];
  const d = Math.hypot(dx, dz);
  if (d >= need) return undefined;
  if (d < 1e-6) return [a[k + 1] + need, a[k + 2]];
  return [a[k + 1] + (dx / d) * need, a[k + 2] + (dz / d) * need];
}

function pushOutOfBox(a: Float32Array, k: number, x: number, z: number, radius: number): [number, number] | undefined {
  const cx = a[k + 1], cz = a[k + 2], ux = a[k + 3], uz = a[k + 4], hw = a[k + 5], hd = a[k + 6];
  // In the box's frame: u along (ux, uz), v along (-uz, ux).
  const dx = x - cx, dz = z - cz;
  const u = dx * ux + dz * uz, v = -dx * uz + dz * ux;
  const qu = Math.max(-hw, Math.min(hw, u)), qv = Math.max(-hd, Math.min(hd, v));
  const ou = u - qu, ov = v - qv;
  const d = Math.hypot(ou, ov);
  let nu: number, nv: number;
  if (d > 1e-6) {
    if (d >= radius) return undefined;
    nu = qu + (ou / d) * radius; nv = qv + (ov / d) * radius;
  } else {
    // Inside: out through the nearest side.
    const toU = hw - Math.abs(u), toV = hd - Math.abs(v);
    if (toU < toV) { nu = Math.sign(u || 1) * (hw + radius); nv = v; } else { nu = u; nv = Math.sign(v || 1) * (hd + radius); }
  }
  return [cx + nu * ux - nv * uz, cz + nu * uz + nv * ux];
}

/**
 * Push a circle out of a polygon outline: to the nearest point on its edges,
 * and the radius beyond, outward — from inside as well as from overlapping it.
 */
function pushOutOfPolygon(points: Float32Array, first: number, count: number, x: number, z: number, radius: number): [number, number] | undefined {
  let inside = false;
  let best = Infinity, qx = 0, qz = 0;
  for (let i = 0, j = count - 1; i < count; j = i++) {
    const ax = points[(first + j) * 2], az = points[(first + j) * 2 + 1];
    const bx = points[(first + i) * 2], bz = points[(first + i) * 2 + 1];
    if ((bz > z) !== (az > z) && x < ((ax - bx) * (z - bz)) / (az - bz) + bx) inside = !inside;
    const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2)) : 0;
    const cx = ax + ex * t, cz = az + ez * t;
    const d = Math.hypot(x - cx, z - cz);
    if (d < best) { best = d; qx = cx; qz = cz; }
  }
  if (!inside && best >= radius) return undefined;
  const dx = x - qx, dz = z - qz;
  const d = Math.hypot(dx, dz);
  if (d < 1e-6) return undefined;
  // Outward is away from the edge point when outside, toward and past it when inside.
  const sign = inside ? -1 : 1;
  return [qx + (dx / d) * radius * sign, qz + (dz / d) * radius * sign];
}

